import { defineComponent, IDatalayerContext, IOmniaShell, isArrayEmpty, labels, LayerViewModel, omniaResolve, selectMany } from "../../omnia"
import { IDatalayerMapDependencies, IDatalayersService, IFieldsService } from "../../types"
import { SatelliteApi } from "../api"
import { SatelliteContext } from "../context"
import { getDefaultDay, ImageryDay, isRgb, setupDays } from "../day"
import { SatelliteMap } from "../map"
import { ImageryProviderId, providers } from "../providers"
import { Imagery, SatImageType } from "../types"
import { CroppingStyle } from "./croppingStyle"
import { createFilter } from "./filter"
import { Preview } from "./preview"

let lastState = {
    date: undefined as Date,
    provider: undefined as ImageryProviderId,
    imageType: undefined as SatImageType
}

export class MainCtrl extends omnia.shell.UpdatableComponent {

    static create(): ng.IDirective {

        const def = defineComponent(MainCtrl, "app/datalayers/satellite/main/main.html")

        def.link = (scope, element: ng.IAugmentedJQuery, attrs, ctrl: MainCtrl) => ctrl.link(element[0])

        return def

    }

    context: SatelliteContext

    private layerContext: IDatalayerContext

    map: SatelliteMap
    private _croppingStyle: CroppingStyle

    api: SatelliteApi

    provider: ImageryProviderId
    providers: { id: ImageryProviderId, text: string }[]

    imageType: SatImageType
    imageTypes: { id: SatImageType, text: string }[]

    days: ImageryDay[]
    private _selectedDay: ImageryDay

    private _previewRequestId = 0
    private _previews: Preview[]
    public layers: LayerViewModel[]

    private _layerOpacityValue = 0
    public layerOpacity: { opacity: number }

    private filter = createFilter(this.fieldSvc)

    public showClouds = false


    static $inject = ["$scope", "$timeout", "OmniaShell", "OmniaFieldsService", "OmniaDatalayersService", "OmniaDatalayersDependencies"]

    constructor(
        $scope: ng.IScope,
        private $timeout: ng.ITimeoutService,
        readonly shell: IOmniaShell,
        private readonly fieldSvc: IFieldsService,
        private readonly layerSvc: IDatalayersService,
        private readonly mapDependencies: IDatalayerMapDependencies,
        private readonly session = shell.getSession()) {

        super($scope)

        this._icon = "fas fa-satellite-dish"
        this._title = "Satellite imagery"

        this._filter = omnia.fields.createFilter(fieldSvc)

    }

    link(root: HTMLElement) {
        this._rootElement = root
        this.scrollToCurrentDate()
    }

    $onInit() {

        this.map = new SatelliteMap(this.mapDependencies)

        this.layerContext = this.mapDependencies.createContext(() => [])

        const style = CroppingStyle.setup(this.map.map)
        this._croppingStyle = style.style
        this.addDisposable(style.dispose)

        this.addDisposable(() => this.removePreviews())

        this.api = new SatelliteApi(this.session, this.fieldSvc, this.layerSvc)

        this.setupAutoRefresh(() => this.refresh(), true, "vm.api.stateVersion")

        this.$scope.$on("omnia.fields.filter", () => this.updateVisibleFields())

    }

    saveState() {

        const { selectedDay, provider, imageType } = this

        lastState = {
            date: selectedDay?.date,
            provider,
            imageType
        }

    }

    refresh(auto = true) {

        const { api } = this

        return api.refresh(auto).then(() => {


            this.providers = api.getProviders()
                .map(id => ({
                    id,
                    text: providers[id].name
                }))

            this.provider ??= this.providers[0]?.id ?? "sh"

            this.setupImageTypes()

            this.setupDates()

            this.scrollToCurrentDate()

            this.updateVisibleFields()

        })

    }

    canViewClouds() {
        return isRgb(this.imageType) && this.api.canViewClouds
    }

    toggleClouds() {
        this.showClouds = !this.showClouds
        this.updateOnTypeChanged()
        this.scrollToCurrentDate()
    }

    private scrollToCurrentDate() {

        if (this._rootElement && this.selectedDay) {

            this.shell.timeout(250).then(() => {

                const element = this._rootElement.querySelector(".timeline > .selected-loc")

                if (element) {
                    element.scrollIntoView({
                        behavior: "smooth",
                        block: "center"
                    })
                }

            })

        }

    }

    private setupImageTypes() {

        const { api, provider } = this

        this.imageTypes = api.getTypes(provider)
            .map(id => ({
                id,
                text: labels.SatImageType(id)
            }))

        const imageType = this.imageType || lastState.imageType

        this.imageType = this.imageTypes.some(t => t.id == imageType)
            ? imageType
            : SatImageType.RGB


    }

    private setupDates() {

        const { api, provider, imageType, selectedDay } = this

        const current = selectedDay?.date ?? lastState.date

        let dates = api.getDates(provider, imageType) ?? []

        if (!this.showClouds) {
            dates = dates.filter(d => !d.rgbOnly)
        }

        const days = this.days = setupDays(dates)

        const day = (current ? days.find(d => d.date.valueOf() == current.valueOf()) : undefined) || getDefaultDay(days)

        this.selectDay(day)

    }

    get selectedDay() {
        return this._selectedDay
    }

    selectDay(day: ImageryDay) {

        this._selectedDay = day

        this.saveState()

        if (day) {

            const { api, provider, imageType } = this

            const requestId = ++this._previewRequestId

            api.getImageryOnDate(provider, imageType, day.date).then(p => {

                if (requestId == this._previewRequestId) {
                    this.displayPreviews(p)
                }

            })

        }

    }

    hasMultipleProviders() {
        return this.providers?.length > 1
    }

    updateOnProviderChanged() {
        this.setupImageTypes()
        this.updateOnTypeChanged()
    }

    updateOnTypeChanged() {
        this.setupDates()
        this.saveState()
    }

    canCreateLayers() {
        return  Boolean(this._selectedDay)
    }

    hasAvailableImagery() {
        return !isArrayEmpty(this.days)
    }

    private displayPreviews(previews: Imagery[]) {

        const { $timeout, filter } = this
        const map = this.map.map.map

        this.removePreviews()

        this._previews = previews.map((p, index) => {

            const group = Math.floor(index / 20)

            // This is a very lazy way to try and avoid bursts of requests to the API,
            // which could cause 429 errors
            // This should be changed to sequences of promises at some point
            // - i.e., a single "all" promise would be created for each group,
            // and then each resulting promise would be processed in sequence
            // More generally, check with the latest versions of OpenLayers
            // to see if there are other ways to achieve this directly at ImageSource level
            const setup = group > 0
                ? $timeout(group * 250)
                : omniaResolve(undefined)

            const d = {
                setup,
                cancel: () => group > 0 ? $timeout.cancel(setup as ng.IPromise<any>) : {}
            }

            return Preview.create(p, map, d, filter.isFieldVisible(p.fieldId))

        })

        this._croppingStyle.setSelectedFields(this._previews.map(p => p.fieldId))

        const layerIds = selectMany(previews.map(p => (p.layers || []).map(l => l.layerId).filter(id => !!id)))

        this.api.getLayers(layerIds).then(layers => {

            const lvm = this.layers = layers.map(l => new LayerViewModel(this.layerContext, this.map.map, l))

            for (const l of lvm) {
                l.visible = filter.isCroppingVisible(l.croppingId)
                l.opacity = this._layerOpacityValue
            }

            const self = this

            this.layerOpacity = lvm.length
                ? {

                    get opacity() {
                        return self._layerOpacityValue
                    },

                    set opacity(value) {
                        self._layerOpacityValue = value
                        for (const l of lvm) {
                            l.opacity = value
                        }
                    }
                }
                : undefined

        })

    }

    private removePreviews() {

        const { map, _previews, layers: _layers } = this

        Preview.dispose(_previews, map?.map?.map)

        if (_layers) {
            for (const l of _layers) {
                l.dispose()
            }
        }

        this._previews = undefined
        this.layers = undefined
        this.layerOpacity = undefined

    }

    private updateVisibleFields() {
        const { _previews: previews = [], layers = [], filter } = this
        filter.update()
        filter.apply(previews, layers)
    }

    private closeCurrentComponent(id?: "layers" | "boundaries") {

        if ((!id) || (this.context?.id == id)) {
            this.map.disableSelection()
            this.context = undefined
            return true
        }

    }


    setupBoundaries() {

        if (this.closeCurrentComponent("boundaries")) {
            return
        }

        const { api, map, days, selectedDay } = this

        this.context = new SatelliteContext(
            "boundaries",
            api,
            map,
            days,
            selectedDay,
            undefined,
            undefined,
            undefined,
            () => this.closeCurrentComponent()
        )

    }

    createLayers(day: ImageryDay) {

        if (this.closeCurrentComponent("layers")) {
            return
        }

        const { api, provider, imageType } = this

        api.getImageryOnDate(provider, imageType, day.date).then(imagery => {

            this.context = new SatelliteContext(
                "layers",
                api,
                this.map,
                this.days,
                this.selectedDay,
                imagery,
                provider,
                imageType,
                () => this.closeCurrentComponent()
            )

        })

    }


}
